import { LANGS, isLang, messages } from "../i18n/index";
import type { Clock } from "../sim/clock";
import { button, localize, playButton, sectionTitle, setText, storeSwitch, switchRow } from "./dom";
import { formatJd, formatSpeed } from "./format";
import { speedFromSlider, sliderFromSpeed } from "./speed";
import type { Store } from "./store";

export interface ControlsDeps {
  clock: Clock;
  store: Store;
  onNow(): void;
  /** Back to the Sun overview with every planet orbit in view. */
  onReset(): void;
}

/** A dropdown of every language, each named in itself. */
function languageSelect(store: Store): HTMLLabelElement {
  const row = document.createElement("label");
  row.className = "select-row";
  const text = document.createElement("span");
  const select = document.createElement("select");
  select.className = "select";
  for (const lang of LANGS) {
    const option = document.createElement("option");
    option.value = lang;
    option.lang = lang;
    option.textContent = messages(lang).name;
    select.append(option);
  }
  select.addEventListener("change", () => {
    if (isLang(select.value)) store.set({ lang: select.value });
  });
  localize(store, (m, lang) => {
    setText(text, m.controls.language);
    select.value = lang;
  });
  row.append(text, select);
  return row;
}

/**
 * Fullscreen for the whole page, or null where the browser has no Fullscreen
 * API (iPhone). Not in the store: the browser owns this state, Esc and F11
 * change it too, and it cannot come back after a reload without a gesture.
 */
function fullscreenSwitch(store: Store): HTMLLabelElement | null {
  if (!document.fullscreenEnabled) return null;
  const { row, input } = switchRow((m) => m.controls.fullscreen, store);
  const sync = (): void => {
    input.checked = document.fullscreenElement !== null;
  };
  input.addEventListener("change", () => {
    const request = input.checked
      ? document.documentElement.requestFullscreen()
      : document.exitFullscreen();
    // the browser said no: show the state it kept
    request.catch(sync);
  });
  document.addEventListener("fullscreenchange", sync);
  sync();
  return row;
}

/** Fills the Controls tab: time, speed, Reset view, the view switches and the language. */
export function mountControls(
  panel: HTMLElement,
  { clock, store, onNow, onReset }: ControlsDeps,
): { refresh(): void } {
  const play = playButton(() => {
    clock.paused = !clock.paused;
    refresh();
  }, store);
  const date = document.createElement("span");
  date.className = "date";
  const time = document.createElement("div");
  time.className = "controls-time";
  const now = button("", onNow, "pill-button");
  time.append(play.el, date, now);

  const slider = document.createElement("input");
  slider.type = "range";
  slider.min = "0";
  slider.max = "1000";
  slider.addEventListener("input", () => {
    clock.speed = speedFromSlider(Number(slider.value) / 1000);
    refresh();
  });
  const speedText = document.createElement("span");
  speedText.className = "speed";
  const speed = document.createElement("div");
  speed.className = "controls-speed";
  speed.append(slider, speedText);

  const reset = button("", onReset, "pill-button reset-view");
  panel.append(
    sectionTitle(store, (m) => m.controls.time),
    time,
    speed,
    reset,
    sectionTitle(store, (m) => m.controls.view),
    storeSwitch(
      (m) => m.controls.trueScale,
      store,
      (s) => s.scale === "true",
      (on) => ({ scale: on ? "true" : "visual" }),
    ),
    storeSwitch(
      (m) => m.controls.labels,
      store,
      (s) => s.labels,
      (on) => ({ labels: on }),
    ),
    storeSwitch(
      (m) => m.controls.orbits,
      store,
      (s) => s.orbits,
      (on) => ({ orbits: on }),
    ),
    ...[fullscreenSwitch(store)].filter((row) => row !== null),
    languageSelect(store),
  );

  const refresh = (): void => {
    play.sync(clock.paused);
    setText(date, formatJd(clock.jd));
    setText(speedText, formatSpeed(clock.speed, store.state.lang));
    slider.value = String(Math.round(sliderFromSpeed(clock.speed) * 1000));
  };
  localize(store, (m) => {
    setText(now, m.controls.now);
    setText(reset, m.controls.resetView);
    slider.setAttribute("aria-label", m.controls.speed);
    refresh();
  });
  return { refresh };
}
